import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import type { RBACGroup, RBACUser } from '../../../types';
import { copyableIds } from '../copyRoles';

import UserSummary from './UserSummary';

type Props = {
  primaryUser: RBACUser;
  referenceUser: RBACUser;
  referenceRoles: RBACGroup[];
  primaryRoles: RBACGroup[];
  isLoadingRoles: boolean;
  loadError: boolean;
  onRetryLoad: () => void;
  selectedIds: ReadonlySet<string>;
  rolesToCopy: RBACGroup[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
};

// Step 2 of Copy Roles: choose which of the peer's roles to copy and preview the result.
// Roles the primary user already holds are shown but not selectable (re-applying is a
// no-op — see deriveRolesToCopy), so the checkboxes only ever stage genuinely new roles.
export default function RoleCopyReview({
  primaryUser,
  referenceUser,
  referenceRoles,
  primaryRoles,
  isLoadingRoles,
  loadError,
  onRetryLoad,
  selectedIds,
  rolesToCopy,
  onToggle,
  onSelectAll,
  onClear,
}: Props) {
  // The peer's roles the primary doesn't already hold — the only ones the header checkbox
  // can toggle. Reuse copyableIds (copyRoles.ts) so "what counts as copyable" is defined
  // once: a row is `held` iff it's not in this set, and the header tristate reads off it.
  const copyable = useMemo(
    () => copyableIds(referenceRoles, primaryRoles),
    [referenceRoles, primaryRoles]
  );
  // How many copyable roles are selected drives the header: all → checked (click clears),
  // some → indeterminate, none → unchecked (click selects all).
  const selectedCopyableCount = referenceRoles.reduce(
    (count, role) => (copyable.has(role.id) && selectedIds.has(role.id) ? count + 1 : count),
    0
  );
  const allSelected = copyable.size > 0 && selectedCopyableCount === copyable.size;
  const someSelected = selectedCopyableCount > 0 && !allSelected;

  // Apply indeterminate via a callback ref (not a useRef + effect keyed on someSelected):
  // the callback runs when the checkbox first mounts — which is after the roles finish
  // loading, a render later than this component — and again whenever someSelected changes.
  // An effect keyed on [someSelected] alone would skip the mount if the value hadn't moved.
  const setSelectAllRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = someSelected;
    },
    [someSelected]
  );

  if (isLoadingRoles) {
    return (
      <div className="p-4 text-sm opacity-60">
        Loading {referenceUser.displayName}&apos;s roles…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-start gap-3 p-4 text-sm text-error">
        <span>Couldn&apos;t load {referenceUser.displayName}&apos;s roles.</span>
        <button type="button" className="btn btn-sm btn-outline btn-error" onClick={onRetryLoad}>
          Try again
        </button>
      </div>
    );
  }

  if (referenceRoles.length === 0) {
    return (
      <div className="p-4 text-sm opacity-60">
        {referenceUser.displayName} holds no direct roles to copy.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-l-4 border-primary bg-base-200 rounded-r-md px-3 py-2 mb-4 flex-none flex items-center justify-start gap-8">
        <div className="min-w-0">
          <UserSummary label="Copying roles from" user={referenceUser} />
        </div>
        <div className="flex-none text-3xl opacity-50" aria-hidden="true">
          →
        </div>
        <div className="min-w-0">
          <UserSummary label="to" user={primaryUser} />
        </div>
      </div>

      <div className="border border-base-200 rounded-lg overflow-hidden mb-3 flex-1 min-h-0 flex flex-col">
        <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] border-b border-base-200 bg-base-200 text-sm font-semibold flex-none">
          <div className="flex items-center justify-center border-r border-base-200 py-2">
            <input
              ref={setSelectAllRef}
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={allSelected}
              disabled={copyable.size === 0}
              onChange={() => (allSelected ? onClear() : onSelectAll())}
              aria-label="Select all roles"
            />
          </div>
          <div className="flex items-center px-3 py-2">Role Group Name</div>
        </div>
        <ul className="divide-y divide-base-200 flex-1 overflow-y-auto">
          {referenceRoles.map((role) => {
            // Held (already assigned) is the exact complement of copyable within the peer's roles.
            const held = !copyable.has(role.id);
            return (
              <li key={role.id}>
                <label
                  className={classNames('grid grid-cols-[2.75rem_minmax(0,1fr)]', {
                    'cursor-not-allowed': held,
                    'cursor-pointer hover:bg-base-200': !held,
                  })}
                >
                  <div className="flex items-center justify-center border-r border-base-200 py-2">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedIds.has(role.id)}
                      disabled={held}
                      onChange={() => onToggle(role.id)}
                      aria-label={`Copy ${role.displayName}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className={held ? 'opacity-50' : ''}>{role.displayName}</span>
                    {held && (
                      <span className="badge badge-ghost badge-sm ml-auto">Already assigned</span>
                    )}
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="bg-base-200 rounded-lg p-3 text-sm flex-none">
        {rolesToCopy.length === 0 ? (
          <span className="opacity-60">
            Nothing to add — {primaryUser.displayName} already holds all selected roles.
          </span>
        ) : (
          <>
            <div className="font-medium mb-2">
              Will add {rolesToCopy.length} role{rolesToCopy.length === 1 ? '' : 's'} to{' '}
              {primaryUser.displayName}:
            </div>
            <ul className="flex flex-col gap-1 max-h-64 overflow-y-auto">
              {rolesToCopy.map((role) => (
                <li key={role.id}>
                  <span className="badge badge-success badge-sm">{role.displayName}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
